import { useState, useEffect } from "react";
import BaseModal from "../BaseComponents/BaseModal/BaseModal";
import styles from "./AddTravelerModal.module.css";

// ── Constants ──────────────────────────────────────────────────────────────
const TITLES        = ["Mr", "Mrs", "Ms", "Dr", "Prof", "Mstr", "Miss"];
const GENDERS       = ["Male", "Female", "Other", "Prefer not to say"];
const NATIONALITIES = [
  "American", "Austrian", "Belgian", "British", "Canadian", "Croatian",
  "Czech", "Danish", "Dutch", "Egyptian", "Finnish", "French", "German",
  "Greek", "Hungarian", "Irish", "Israeli", "Italian", "Japanese",
  "Lebanese", "Moroccan", "Norwegian", "Polish", "Portuguese", "Romanian",
  "Russian", "South African", "Spanish", "Swedish", "Swiss", "Turkish",
  "Tunisian", "Ukrainian",
].sort();

// ── Helpers ────────────────────────────────────────────────────────────────
const calculateAge = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const getTravelerType = (age) => {
  if (age === null) return null;
  if (age <= 1)     return { label: "Infant",  color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", icon: "👶" };
  if (age <= 11)    return { label: "Child",   color: "#d97706", bg: "#fff7ed", border: "#fed7aa", icon: "👦" };
  return              { label: "Adult",   color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", icon: "🧑" };
};

const emptyForm = {
  title:           "Mr",
  firstName:       "",
  lastName:        "",
  dob:             "",
  gender:          "",
  nationality:     "",
};

const REQUIRED = ["firstName", "lastName", "dob", "nationality"];

const validateField = (key, value) => {
  switch (key) {
    case "firstName":
      if (!value?.trim())          return "First name is required";
      if (value.trim().length < 2) return "At least 2 characters";
      break;
    case "lastName":
      if (!value?.trim()) return "Last name is required";
      break;
    case "dob":
      if (!value) return "Date of birth is required";
      if (new Date(value) > new Date()) return "Date of birth cannot be in the future";
      break;
    case "nationality":
      if (!value) return "Nationality is required";
      break;
    default: break;
  }
  return "";
};

// ── Component ──────────────────────────────────────────────────────────────
const AddTravelerModal = ({
  open,
  onClose,
  onSave,
  traveler = null,  // null = Add mode; object = Edit mode
}) => {
  const [form,    setForm]    = useState(emptyForm);
  const [errors,  setErrors]  = useState({});
  const [touched, setTouched] = useState({});

  const isEdit = !!traveler;

  // ── Derived: live age + type from DOB ──
  const liveAge  = calculateAge(form.dob);
  const liveType = getTravelerType(liveAge);

  // ── Populate on open ──
  useEffect(() => {
    if (!open) return;
    if (traveler) {
      setForm({
        title:             traveler.title            ?? "Mr",
        firstName:         traveler.firstName        ?? "",
        lastName:          traveler.lastName         ?? "",
        dob:               traveler.dob              ?? "",
        gender:            traveler.gender           ?? "",
        nationality:       traveler.nationality      ?? "",
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
  }, [open, traveler]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: "" }));
  };

  const handleBlur = (key) => {
    setTouched(prev => ({ ...prev, [key]: true }));
    setErrors(prev => ({ ...prev, [key]: validateField(key, form[key]) }));
  };

  const handleClose = () => {
    setForm(emptyForm); setErrors({}); setTouched({});
    onClose();
  };

  const handleSubmit = () => {
    const newErrors = {};
    let isValid = true;
    REQUIRED.forEach(key => {
      const err = validateField(key, form[key]);
      if (err) { newErrors[key] = err; isValid = false; }
    });
    setErrors(newErrors);
    setTouched(Object.fromEntries(REQUIRED.map(k => [k, true])));
    if (!isValid) return;

    onSave({
      ...(traveler ?? {}),
      id:             traveler?.id ?? Date.now(),
      ref:            traveler?.ref ?? `TRVL-${String(Math.floor(Math.random() * 90000) + 10000)}`,
      title:          form.title,
      firstName:      form.firstName.trim(),
      lastName:       form.lastName.trim(),
      dob:            form.dob,
      gender:         form.gender      || undefined,
      nationality:    form.nationality,
      age:            liveAge,
      travelerType:   liveType?.label ?? "Adult",
    });
    handleClose();
  };

  const hasErrors = Object.values(errors).some(Boolean);

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={isEdit ? "Edit Traveler" : "Add Traveler Profile"}
      width="640px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={handleClose}>Cancel</button>
          <button className={styles.saveBtn} onClick={handleSubmit}>
            {isEdit ? "Save Changes" : "Add Traveler"}
          </button>
        </>
      }
    >

      {/* ── Avatar Preview Header ── */}
      <div className={styles.avatarHeader}>
        <div
          className={styles.avatarPreview}
          style={{ background: liveType ? liveType.color : "#9ca3af" }}
        >
          {form.firstName && form.lastName
            ? `${form.firstName[0]}${form.lastName[0]}`.toUpperCase()
            : "?"}
        </div>
        <div className={styles.avatarHeaderInfo}>
          <div className={styles.avatarHeaderName}>
            {[form.title, form.firstName, form.lastName].filter(Boolean).join(" ") || "New Traveler"}
          </div>
          {liveType ? (
            <span
              className={styles.liveTypeBadge}
              style={{ background: liveType.bg, color: liveType.color, border: `1px solid ${liveType.border}` }}
            >
              {liveType.icon} {liveType.label}
              {liveAge !== null && <span className={styles.liveAge}> · {liveAge} yrs old</span>}
            </span>
          ) : (
            <span className={styles.liveTypePlaceholder}>Type calculated automatically from DOB</span>
          )}
        </div>
      </div>

      {/* ── Business Rule Notice ── */}
      <div className={styles.ruleNotice}>
        <span>⚠️</span>
        <div>
          <strong>Traveler ≠ Customer.</strong> Travelers never pay. Age type (ADT/CHD/INF) is
          always calculated from date of birth and cannot be set manually.
          All financial data lives on the Customer record only.
        </div>
      </div>

      {/* ── Error Banner ── */}
      {hasErrors && (
        <div className={styles.errorBanner}>
          <span>⚠️</span>
          <div>
            <strong>Please fix the following:</strong>
            <ul>{Object.entries(errors).map(([k,e]) => e ? <li key={k}>{e}</li> : null)}</ul>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* Section 1 — Personal Details                                 */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>👤 PERSONAL DETAILS</h4>
        <div className={styles.formGrid}>

          {/* Title */}
          <div className={styles.field} style={{ gridColumn: "1" }}>
            <label>TITLE</label>
            <select value={form.title} onChange={e => handleChange("title", e.target.value)}>
              {TITLES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>

          {/* First Name */}
          <div className={styles.field}>
            <label>FIRST NAME <span className={styles.req}>*</span></label>
            <input
              value={form.firstName}
              onChange={e => handleChange("firstName", e.target.value)}
              onBlur={() => handleBlur("firstName")}
              placeholder="e.g. Thomas"
              className={errors.firstName ? styles.inputErr : ""}
            />
            {errors.firstName && <span className={styles.errText}>{errors.firstName}</span>}
          </div>

          {/* Last Name */}
          <div className={styles.field}>
            <label>LAST NAME <span className={styles.req}>*</span></label>
            <input
              value={form.lastName}
              onChange={e => handleChange("lastName", e.target.value.toUpperCase())}
              onBlur={() => handleBlur("lastName")}
              placeholder="e.g. LECLERCQ"
              style={{ textTransform: "uppercase" }}
              className={errors.lastName ? styles.inputErr : ""}
            />
            {errors.lastName && <span className={styles.errText}>{errors.lastName}</span>}
          </div>

          {/* Gender */}
          <div className={styles.field}>
            <label>GENDER</label>
            <select value={form.gender} onChange={e => handleChange("gender", e.target.value)}>
              <option value="">Select gender</option>
              {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>

          {/* Nationality */}
          <div className={styles.field}>
            <label>NATIONALITY <span className={styles.req}>*</span></label>
            <select
              value={form.nationality}
              onChange={e => handleChange("nationality", e.target.value)}
              onBlur={() => handleBlur("nationality")}
              className={errors.nationality ? styles.inputErr : ""}
            >
              <option value="">Select nationality</option>
              {NATIONALITIES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            {errors.nationality && <span className={styles.errText}>{errors.nationality}</span>}
          </div>

        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* Section 2 — Date of Birth (key field — drives type)          */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>🎂 DATE OF BIRTH</h4>

        <div className={styles.dobRow}>
          <div className={styles.field} style={{ flex: 1 }}>
            <label>DATE OF BIRTH <span className={styles.req}>*</span></label>
            <input
              type="date"
              value={form.dob}
              onChange={e => handleChange("dob", e.target.value)}
              onBlur={() => handleBlur("dob")}
              max={new Date().toISOString().split("T")[0]}
              className={errors.dob ? styles.inputErr : ""}
            />
            {errors.dob && <span className={styles.errText}>{errors.dob}</span>}
          </div>

          {/* Live type classification */}
          <div className={styles.dobResult}>
            {liveType ? (
              <div className={styles.dobResultInner}>
                <div className={styles.dobResultLabel}>Classified as</div>
                <span
                  className={styles.dobResultBadge}
                  style={{ background: liveType.bg, color: liveType.color, border: `1px solid ${liveType.border}` }}
                >
                  {liveType.icon} {liveType.label}
                </span>
                <div className={styles.dobResultAge}>Age: {liveAge} year{liveAge !== 1 ? "s" : ""} old</div>
                <div className={styles.dobResultRule}>
                  {liveType.label === "Infant" && "Under 2 years"}
                  {liveType.label === "Child"  && "2 – 11 years"}
                  {liveType.label === "Adult"  && "12 years or older"}
                </div>
              </div>
            ) : (
              <div className={styles.dobResultEmpty}>
                <div className={styles.dobResultLabel}>Enter a date of birth</div>
                <div className={styles.dobRules}>
                  <div className={styles.dobRule}><span className={styles.dotGreen}/>Infant: under 2</div>
                  <div className={styles.dobRule}><span className={styles.dotAmber}/>Child: 2 – 11</div>
                  <div className={styles.dobRule}><span className={styles.dotBlue} />Adult: 12+</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>


    </BaseModal>
  );
};

export default AddTravelerModal;